import { addMonths, format, isAfter, subDays } from 'date-fns';
import { StyleSheet, Text, View } from 'react-native';

import { useLocale } from 'state/intl';
import { t, tRich } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { localeStringToDateFnsLocale } from 'utils/intl/locale-string-to-date-fns-locale';

interface ExpirationWarningProps {
  pointsToExpire: number;
  expirationDate: string;
}

export const ExpirationWarning = ({ pointsToExpire, expirationDate }: ExpirationWarningProps) => {
  const { locale } = useLocale();

  const date = new Date(expirationDate);

  // If the date is over three months from now, don't show the component
  const threeMonthsFromNow = addMonths(new Date(), 3);
  if (isAfter(date, threeMonthsFromNow)) {
    return null;
  }

  const dateFormat = 'MMMM do';
  const utc = new Date(date.toUTCString().substring(0, 25));

  // Subtract 1 day to reflect the displayed expiration date
  const subtractedDate = subDays(utc, 1);

  const formattedDate = format(subtractedDate, dateFormat, {
    locale: localeStringToDateFnsLocale(locale),
  });

  const youHaveText = t('youHave');
  const crownsText = t('crowns');
  const expiringText = t('expiring');

  const loyaltyPointsExpiringText = t('loyaltyPointsExpiring', { points: pointsToExpire });
  const loyaltyPointsUseThemByText = tRich('loyaltyPointsUseThemBy', {
    date: <Text style={styles.boldText}>{formattedDate}</Text>,
  });

  const accessibilityLabel = `${youHaveText} ${pointsToExpire} ${crownsText} ${expiringText} ${formattedDate}`;

  return (
    <View
      style={styles.expirationWarning}
      accessible={true}
      accessibilityRole="alert"
      accessibilityLabel={accessibilityLabel}
      accessibilityLiveRegion="polite"
    >
      <Text style={styles.expirationText}>
        <Text style={styles.boldText}>{loyaltyPointsExpiringText}</Text>{' '}
        <Text>{loyaltyPointsUseThemByText}</Text>
      </Text>
    </View>
  );
};

export const styles = StyleSheet.create({
  expirationWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: primitive.$blackOpacity4,
    paddingVertical: spacing.$2,
    paddingHorizontal: spacing.$4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  expirationText: {
    fontSize: primitive.fontSizes.xs,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    lineHeight: 18,
    flex: 1,
  },
  boldText: {
    fontFamily: primitive.fontFamily.$headers,
  },
});
